import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { RootState } from '../../../appReducer'
import { useAppDispatch } from '../../../util/useAppDispatch'
import {
    fetchLocationsSchedulingData,
    fetchPracticeLocationProcedures,
    fetchPracticeWithLocations,
    fetchProcedureProviders,
} from '../../practices/actions'
import { OnSendMessageParams } from '../chat-tiles-container-hooks/useMessaging'

import {
    appointmentSubscribeToChannel,
    connectToAppointmentSocket,
    createAppointment,
    disconnectFromAppointmentSocket,
    resetForm,
    resetSearchResults,
    selectExistingPatient,
    setAppointmentBookingStep,
    setAppointmentDatetime,
    setAppointmentPickerError,
    setBookingError,
    setPending,
    updateFormFields,
    updateSearchFormFields,
} from './actions'
import { DirectSchedulingContextConsumer, DirectSchedulingContextProvider } from './DirectSchedulingContext'
import PatientDetailTab from './PatientDetail'

import './DirectScheduling.sass'

export type Props = {
    chat: Models.ChatCenterSelectedChat
    open: boolean
    webCode: Models.WebCode
    onSendMessage: (sendMessageParams: OnSendMessageParams) => void
}

const moduleName = 'direct-scheduling-appointment'

const DirectScheduling = (props: Props) => {
    const { webCode, open, chat } = props
    const practice = useSelector((state: RootState) => state.practices.practices[props.chat.practice.id])
    const booking = useSelector((state: RootState) => state.bookings.directScheduling[props.chat.id])
    const chatConversation = useSelector((state: RootState) => state.chat.conversations[props.chat.channelName])

    const chatHasScheduledAppointment = chatConversation?.messages.some(
        message => message.text.includes('Wonderful! I have booked an appointment for') && !message.is_patient,
    )

    const [locationsSchedulingDataLoaded, setLocationsSchedulingDataLoaded] = useState(false)

    const directSchedulingRef = useRef<HTMLDivElement>(null)

    const dispatch = useAppDispatch()

    useEffect(() => {
        dispatch(connectToAppointmentSocket(chat))

        return () => {
            dispatch(disconnectFromAppointmentSocket(chat))
        }
    }, [dispatch])

    useEffect(() => {
        async function fetchLocations() {
            const loadedPractice = await dispatch(fetchPracticeWithLocations(chat.practice.id))

            if (!loadedPractice) {
                return
            }

            await dispatch(fetchLocationsSchedulingData(loadedPractice.id))
            setLocationsSchedulingDataLoaded(true)
        }

        if (chat.practice.id) {
            fetchLocations()
        }
    }, [chat.practice.id, dispatch])

    if (!booking) {
        return <div>Loading...</div>
    }

    const handleSendMessage = (message: string) => {
        props.onSendMessage({
            message,
            chatMetadata: chat,
            channel: chat.channelName,
        })
    }

    const handleAddPatient = () => {
        dispatch(resetForm(chat, true))
    }

    return (
        <div className={moduleName} ref={directSchedulingRef}>
            {booking.isAdditionalPatientBooking && <div className={`${moduleName}__heading`}>Additional Patient</div>}

            <DirectSchedulingContextProvider
                value={{
                    chat,
                    webCode,
                    createAppointment: createAppointment,
                    fetchPracticeLocationProcedures: fetchPracticeLocationProcedures,
                    fetchProcedureProviders: fetchProcedureProviders,
                    onSendMessage: handleSendMessage,
                    practice,
                    resetForm: resetForm,
                    selectExistingPatient: selectExistingPatient,
                    setAppointmentBookingStep: setAppointmentBookingStep,
                    setAppointmentDatetime: setAppointmentDatetime,
                    setAppointmentPickerError: setAppointmentPickerError,
                    appointmentSubscribeToChannel: appointmentSubscribeToChannel,
                    setPending: setPending,
                    updateFormFields: updateFormFields,
                    updateSearchFormFields: updateSearchFormFields,
                    handleAddPatient,
                    setBookingError: setBookingError,
                }}
            >
                <DirectSchedulingContextConsumer>
                    {context =>
                        context && (
                            <PatientDetailTab
                                {...context}
                                isModalOpened={open}
                                form={booking.patient}
                                appointment={booking.appointment}
                                searchPatients={booking.searchPatients}
                                resetSearchResults={resetSearchResults}
                                isNewPatient={booking.isNewPatient}
                                isAdditionalPatientBooking={booking.isAdditionalPatientBooking}
                                chatHasScheduledAppointment={chatHasScheduledAppointment}
                                locationsSchedulingDataLoaded={locationsSchedulingDataLoaded}
                                directSchedulingRef={directSchedulingRef}
                            />
                        )
                    }
                </DirectSchedulingContextConsumer>
            </DirectSchedulingContextProvider>
        </div>
    )
}

export default DirectScheduling
